import { useEffect, useState } from 'react';

import type { PostDraftSet, PostsFromOfferRequest, ProviderId } from '@smartapply/shared';

import { PageShell } from '../components/layout/PageShell.js';
import { DraftSetView } from '../components/posts/DraftSet.js';
import { FromOfferForm } from '../components/posts/FromOfferForm.js';
import { ProviderForm } from '../components/provider/ProviderForm.js';
import { Stamp } from '../components/ui/Stamp.js';
import { fetchProviderCurrent, SmartApplyApiError } from '../lib/api-client.js';
import {
  generatePostsFromLink,
  generatePostsFromOffer,
  generatePostsFromTopic,
} from '../lib/posts-client.js';

type Mode = 'link' | 'topic' | 'offer';

type ProviderStatus =
  | { kind: 'checking' }
  | { kind: 'configured'; provider: ProviderId; modelId: string | null }
  | { kind: 'missing' };

type State =
  | { kind: 'idle' }
  | { kind: 'generating'; mode: Mode }
  | { kind: 'done'; set: PostDraftSet }
  | { kind: 'error'; message: string };

export function PostsRoute() {
  const [mode, setMode] = useState<Mode>('link');
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [providerStatus, setProviderStatus] = useState<ProviderStatus>({ kind: 'checking' });

  // form fields — link
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [linkNote, setLinkNote] = useState('');

  // form fields — modo Tendências (sem tópico — IA descobre)
  const [audience, setAudience] = useState('');
  const [topicHint, setTopicHint] = useState('');

  // Detecta se o provider está configurado server-side. Restart do API ou
  // sessão fria devolve `configured: false`, mesmo com profile cacheado no
  // sessionStorage. Em vez de obrigar reupload do PDF (gasta tokens), abre
  // ProviderForm inline pra user inserir só a key e prosseguir.
  useEffect(() => {
    void (async () => {
      try {
        const res = await fetchProviderCurrent();
        if (res.configured) {
          setProviderStatus({ kind: 'configured', provider: res.provider, modelId: res.modelId });
        } else {
          setProviderStatus({ kind: 'missing' });
        }
      } catch {
        setProviderStatus({ kind: 'missing' });
      }
    })();
  }, []);

  function handleProviderConfigured(_provider: ProviderId, modelId: string) {
    setProviderStatus({ kind: 'configured', provider: _provider, modelId });
  }

  async function generateFromLink() {
    setState({ kind: 'generating', mode: 'link' });
    try {
      const set = await generatePostsFromLink({
        url: linkUrl || undefined,
        text: linkText || undefined,
        note: linkNote || undefined,
      });
      setState({ kind: 'done', set });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof SmartApplyApiError ? err.message : 'Falha ao gerar drafts.',
      });
    }
  }

  async function generateFromTrending() {
    setState({ kind: 'generating', mode: 'topic' });
    try {
      const set = await generatePostsFromTopic({
        audience: audience || undefined,
        hint: topicHint || undefined,
      });
      setState({ kind: 'done', set });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof SmartApplyApiError ? err.message : 'Falha ao gerar drafts.',
      });
    }
  }

  async function generateFromOffer(req: PostsFromOfferRequest) {
    setState({ kind: 'generating', mode: 'offer' });
    try {
      const set = await generatePostsFromOffer(req);
      setState({ kind: 'done', set });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof SmartApplyApiError ? err.message : 'Falha ao gerar drafts.',
      });
    }
  }

  function reset() {
    setState({ kind: 'idle' });
  }

  return (
    <PageShell>
      <header className="mb-12">
        <p className="mb-4 inline-block">
          <Stamp>Sugestão de posts</Stamp>
        </p>
        <h1 className="font-serif text-[30px] font-medium leading-[1.05] tracking-[-0.02em] text-[var(--ink-primary)] sm:text-[40px] lg:text-[44px]">
          Drafts de post
          <span className="text-[var(--accent-saffron)]">.</span>
        </h1>
        <p className="mt-4 max-w-2xl font-sans text-[15px] leading-relaxed text-[var(--ink-secondary)] sm:text-base">
          A IA gera 3-5 drafts no estilo do seu perfil. Use uma URL ou texto como inspiração, ou
          peça pra ela descobrir os tópicos quentes da sua área agora.
        </p>
      </header>

      {providerStatus.kind === 'checking' && (
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          verificando provider...
        </p>
      )}

      {providerStatus.kind === 'missing' && state.kind !== 'done' && (
        <div
          className="mb-8 card-paper rounded-sm px-6 py-6"
          style={{ borderLeft: '3px solid var(--accent-saffron)' }}
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--accent-saffron)]">
            configurar IA pra gerar posts
          </p>
          <p className="mt-2 font-sans text-sm text-[var(--ink-secondary)]">
            Sua análise está persistida nesta aba. Pra gerar posts, só preciso da chave da IA (não
            vou re-processar o PDF, não vai gastar tokens com análise nova). A chave fica só em
            memória da sessão do servidor — nada em banco.
          </p>
          <div className="mt-5">
            <ProviderForm onConfigured={handleProviderConfigured} />
          </div>
        </div>
      )}

      {providerStatus.kind === 'configured' && state.kind === 'done' ? (
        <DraftSetView set={state.set} onReset={reset} />
      ) : providerStatus.kind === 'configured' ? (
        <>
          <div className="mb-8 inline-flex rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper-elevated)] p-1">
            <button
              type="button"
              onClick={() => setMode('link')}
              disabled={state.kind === 'generating'}
              className={`rounded-sm px-4 py-2 font-sans text-sm transition-colors ${
                mode === 'link'
                  ? 'bg-[var(--ink-primary)] text-[var(--bg-paper)]'
                  : 'text-[var(--ink-secondary)]'
              }`}
            >
              A partir de uma URL/texto
            </button>
            <button
              type="button"
              onClick={() => setMode('topic')}
              disabled={state.kind === 'generating'}
              className={`rounded-sm px-4 py-2 font-sans text-sm transition-colors ${
                mode === 'topic'
                  ? 'bg-[var(--ink-primary)] text-[var(--bg-paper)]'
                  : 'text-[var(--ink-secondary)]'
              }`}
            >
              Tendências da minha área (Google/Gemini)
            </button>
            <button
              type="button"
              onClick={() => setMode('offer')}
              disabled={state.kind === 'generating'}
              className={`rounded-sm px-4 py-2 font-sans text-sm transition-colors ${
                mode === 'offer'
                  ? 'bg-[var(--ink-primary)] text-[var(--bg-paper)]'
                  : 'text-[var(--ink-secondary)]'
              }`}
            >
              Lançamento de oferta
            </button>
          </div>

          {mode === 'link' && (
            <FromLinkForm
              url={linkUrl}
              setUrl={setLinkUrl}
              text={linkText}
              setText={setLinkText}
              note={linkNote}
              setNote={setLinkNote}
              onSubmit={() => void generateFromLink()}
              loading={state.kind === 'generating'}
            />
          )}

          {mode === 'topic' && (
            <FromTrendingForm
              audience={audience}
              setAudience={setAudience}
              hint={topicHint}
              setHint={setTopicHint}
              onSubmit={() => void generateFromTrending()}
              loading={state.kind === 'generating'}
            />
          )}

          {mode === 'offer' && (
            <FromOfferForm
              onSubmit={(req) => void generateFromOffer(req)}
              loading={state.kind === 'generating'}
            />
          )}

          {state.kind === 'generating' && <GeneratingIndicator mode={state.mode} />}
          {state.kind === 'error' && (
            <div
              className="mt-6 card-paper rounded-sm px-6 py-5"
              style={{ borderLeft: '3px solid var(--accent-crimson)' }}
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--accent-crimson)]">
                erro
              </p>
              <p className="mt-2 font-sans text-base text-[var(--ink-primary)]">{state.message}</p>
              <button
                type="button"
                onClick={reset}
                className="mt-4 font-sans text-sm text-[var(--ink-secondary)] underline-offset-4 hover:underline"
              >
                tentar de novo
              </button>
            </div>
          )}
        </>
      ) : null}
    </PageShell>
  );
}

function FromLinkForm(props: {
  url: string;
  setUrl: (v: string) => void;
  text: string;
  setText: (v: string) => void;
  note: string;
  setNote: (v: string) => void;
  onSubmit: () => void;
  loading: boolean;
}) {
  const isLinkedIn = /linkedin\.com|lnkd\.in/i.test(props.url);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        props.onSubmit();
      }}
      className="card-paper rounded-sm px-7 py-7"
      style={{ borderLeft: '3px solid var(--accent-saffron)' }}
    >
      <div className="space-y-5">
        <div>
          <label
            htmlFor="link-url"
            className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]"
          >
            URL do post / artigo (opcional)
          </label>
          <input
            id="link-url"
            type="url"
            placeholder="https://..."
            value={props.url}
            onChange={(e) => props.setUrl(e.target.value)}
            className="mt-2 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-mono text-sm text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
          />
          {isLinkedIn && (
            <p className="mt-2 font-sans text-xs text-[var(--accent-saffron)]">
              ⚠ LinkedIn bloqueia fetch automático. Cole o texto do post no campo abaixo.
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="link-text"
            className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]"
          >
            ou cole o texto do post {props.url ? '(usado se URL não funcionar)' : ''}
          </label>
          <textarea
            id="link-text"
            rows={6}
            value={props.text}
            onChange={(e) => props.setText(e.target.value)}
            maxLength={8000}
            placeholder="Cole o texto do post aqui (mínimo 40 chars)..."
            className="mt-2 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-sans text-sm leading-relaxed text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
          />
          <p className="mt-1 font-mono text-[10px] tabular-nums text-[var(--ink-muted)]">
            {props.text.length}/8000 chars
          </p>
        </div>

        <div>
          <label
            htmlFor="link-note"
            className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]"
          >
            nota / ângulo desejado (opcional)
          </label>
          <input
            id="link-note"
            type="text"
            placeholder="Ex: discordo da abordagem, queria comentar do meu ângulo de QA"
            value={props.note}
            onChange={(e) => props.setNote(e.target.value)}
            maxLength={500}
            className="mt-2 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-sans text-sm text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
          />
        </div>

        <button
          type="submit"
          disabled={props.loading || (!props.url && !props.text)}
          className="rounded-sm bg-[var(--ink-primary)] px-5 py-2 font-sans text-sm text-[var(--bg-paper)] transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {props.loading ? 'Gerando drafts...' : 'Gerar 3-5 drafts'}
        </button>
      </div>
    </form>
  );
}

function FromTrendingForm(props: {
  audience: string;
  setAudience: (v: string) => void;
  hint: string;
  setHint: (v: string) => void;
  onSubmit: () => void;
  loading: boolean;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        props.onSubmit();
      }}
      className="card-paper rounded-sm px-7 py-7"
      style={{ borderLeft: '3px solid var(--accent-saffron)' }}
    >
      <div className="space-y-5">
        <div className="rounded-sm border border-[var(--accent-saffron-border)] bg-[var(--bg-suggestion-tint)] px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--accent-saffron)]">
            como funciona
          </p>
          <p className="mt-1 font-sans text-xs text-[var(--ink-secondary)]">
            A IA usa web search nativo do provider pra descobrir tópicos quentes da{' '}
            <strong>sua área</strong> nos últimos 30-90 dias. Não precisa indicar tópico. Ela
            escolhe os 3-5 mais relevantes considerando o seu perfil e gera 1 draft por tópico, com
            fontes citadas.
          </p>
          <p className="mt-2 font-sans text-xs text-[var(--ink-tertiary)]">
            Provider precisa ser <strong>Anthropic</strong> (web search) ou <strong>Google</strong>{' '}
            (Search Grounding). OpenAI e OpenRouter não suportam essa rota — pra esses, use o modo
            URL/texto. Custo da chamada sai da sua chave (~$0.10-$0.40).
          </p>
        </div>

        <div>
          <label
            htmlFor="audience"
            className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]"
          >
            audiência alvo (opcional)
          </label>
          <input
            id="audience"
            type="text"
            placeholder="Ex: tech leads de squad de produto"
            value={props.audience}
            onChange={(e) => props.setAudience(e.target.value)}
            maxLength={200}
            className="mt-2 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-sans text-sm text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
          />
          <p className="mt-1 font-sans text-xs text-[var(--ink-tertiary)]">
            Refina pra quem são os drafts. Vazio = audiência da sua área.
          </p>
        </div>

        <div>
          <label
            htmlFor="topic-hint"
            className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]"
          >
            dica/restrição opcional
          </label>
          <input
            id="topic-hint"
            type="text"
            placeholder="Ex: foque em decisões técnicas controversas, evite tooling gigante"
            value={props.hint}
            onChange={(e) => props.setHint(e.target.value)}
            maxLength={500}
            className="mt-2 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-sans text-sm text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
          />
          <p className="mt-1 font-sans text-xs text-[var(--ink-tertiary)]">
            Filtros leves. A IA decide onde pesquisar.
          </p>
        </div>

        <button
          type="submit"
          disabled={props.loading}
          className="rounded-sm bg-[var(--ink-primary)] px-5 py-2 font-sans text-sm text-[var(--bg-paper)] transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {props.loading ? 'Pesquisando tendências...' : 'Descobrir e gerar drafts'}
        </button>
      </div>
    </form>
  );
}

function GeneratingIndicator({ mode }: { mode: Mode }) {
  return (
    <div className="mt-6 rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper-elevated)] px-6 py-5">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
        {mode === 'topic' ? 'pesquisando tendências' : 'gerando drafts'}
      </p>
      <p className="mt-2 font-serif text-base text-[var(--ink-primary)]">
        {mode === 'topic'
          ? 'Buscando tópicos quentes da sua área e gerando drafts. Pode levar 60-120s.'
          : 'Lendo o conteúdo e gerando drafts no seu estilo. ~15-40s.'}
      </p>
    </div>
  );
}
